import { useState, useEffect, useCallback } from 'react';

export interface CookiePreferences {
  analytics: boolean;
  marketing: boolean;
  necessary: boolean; // Always true
}

const CONSENT_KEY = 'habilou_cookie_consent';
const PREFERENCES_KEY = 'habilou_cookie_preferences';

export const useCookieConsent = () => {
  const [hasConsented, setHasConsented] = useState<boolean | null>(null);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    analytics: false,
    marketing: false,
    necessary: true,
  });
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    const savedPreferences = localStorage.getItem(PREFERENCES_KEY);

    if (consent !== null) {
      setHasConsented(consent === 'true');
      if (savedPreferences) {
        const parsed = JSON.parse(savedPreferences);
        setPreferences(parsed);
        if (parsed.analytics) {
          loadAnalyticsScripts();
        }
      }
    } else {
      setShowBanner(true);
    }
  }, []);

  const loadAnalyticsScripts = useCallback(() => {
    // Load Google Analytics
    if (!document.querySelector('script[src*="googletagmanager"]')) {
      const gtagScript = document.createElement('script');
      gtagScript.async = true;
      gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-Z2JQFT5QSH';
      document.head.appendChild(gtagScript);

      gtagScript.onload = () => {
        window.dataLayer = window.dataLayer || [];
        function gtag(...args: any[]) {
          window.dataLayer.push(args);
        }
        gtag('js', new Date());
        gtag('config', 'G-Z2JQFT5QSH');
        (window as any).gtag = gtag;
      };
    }

    // Load Ahrefs Analytics
    if (!document.querySelector('script[src*="analytics.ahrefs"]')) {
      const ahrefsScript = document.createElement('script');
      ahrefsScript.src = 'https://analytics.ahrefs.com/analytics.js';
      ahrefsScript.dataset.key = 'hV2AJFm7Cj3o1Mq/L+aVJw';
      ahrefsScript.async = true;
      document.head.appendChild(ahrefsScript);
    }
  }, []);

  const acceptAll = useCallback(() => {
    const newPreferences: CookiePreferences = {
      analytics: true,
      marketing: true,
      necessary: true,
    };
    setPreferences(newPreferences);
    setHasConsented(true);
    setShowBanner(false);
    localStorage.setItem(CONSENT_KEY, 'true');
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(newPreferences));
    loadAnalyticsScripts();
  }, [loadAnalyticsScripts]);

  const rejectAll = useCallback(() => {
    const newPreferences: CookiePreferences = {
      analytics: false,
      marketing: false,
      necessary: true,
    };
    setPreferences(newPreferences);
    setHasConsented(false);
    setShowBanner(false);
    localStorage.setItem(CONSENT_KEY, 'false');
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(newPreferences));
  }, []);

  const savePreferences = useCallback((newPreferences: CookiePreferences) => {
    setPreferences(newPreferences);
    setHasConsented(newPreferences.analytics || newPreferences.marketing);
    setShowBanner(false);
    localStorage.setItem(CONSENT_KEY, String(newPreferences.analytics || newPreferences.marketing));
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(newPreferences));
    
    if (newPreferences.analytics) {
      loadAnalyticsScripts();
    }
  }, [loadAnalyticsScripts]);

  const openPreferences = useCallback(() => {
    setShowBanner(true);
  }, []);

  return {
    hasConsented,
    preferences,
    showBanner,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
  };
};

// Add type for dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}
